<template>
  <div class="profile-page">
    <header class="profile-header">
      <div>
        <div class="profile-kicker">Account Center</div>
        <h1>Settings</h1>
        <p>Manage your personal information, sign-in details, and current session.</p>
      </div>

      <div class="profile-status">
        <span class="profile-status-dot"></span>
        <span class="profile-status-copy">
          <small>Account status</small>
          <strong>Authenticated</strong>
        </span>
      </div>
    </header>

    <!-- Notifications first: it is the tab people come to change. ?tab=account
         deep-links the profile, security and session panels. -->
    <nav class="profile-tabs" role="tablist" aria-label="Settings sections">
      <button
        v-for="item in tabs"
        :key="item.key"
        type="button"
        role="tab"
        class="profile-tab"
        :class="{ active: tab === item.key }"
        :aria-selected="tab === item.key ? 'true' : 'false'"
        @click="setTab(item.key)">{{ item.label }}</button>
    </nav>

    <NotificationPreferencesPanel v-if="tab === 'notifications'" />

    <div v-show="tab === 'account'" class="profile-settings-grid">
      <section class="profile-panel profile-panel--identity">
        <div class="profile-panel-topline"></div>

        <div class="profile-section-heading">
          <div>
            <div class="profile-section-label">Profile</div>
            <h2>Personal information</h2>
            <p>This information identifies your saved builds and GixCore account.</p>
          </div>
          <button v-if="!edit" type="button" class="profile-ghost-button" @click="startEdit">
            Edit profile
          </button>
        </div>

        <div class="profile-summary">
          <div class="profile-avatar">
            <img
              v-if="avatarUrl"
              :src="avatarUrl"
              :alt="displayName"
              class="profile-avatar-img"
              referrerpolicy="no-referrer"
              @error="avatarError = true"
            >
            <span v-else>{{ initials }}</span>
          </div>
          <div class="profile-identity">
            <span>GixCore account</span>
            <h2>{{ displayName }}</h2>
            <p>{{ profileUser.email || 'No email on file' }}</p>
          </div>
        </div>

        <form class="profile-form" v-on:submit.prevent="handleSave">
          <div class="profile-grid">
            <div class="profile-field">
              <label for="profile-email">Email</label>
              <input
                v-if="edit"
                id="profile-email"
                v-model="email"
                class="profile-input"
                type="email"
                :placeholder="profileUser.email"
              >
              <span v-else class="profile-value">{{ profileUser.email || '-' }}</span>
            </div>

            <div class="profile-field">
              <label for="profile-username">Username</label>
              <input
                v-if="edit"
                id="profile-username"
                v-model="username"
                class="profile-input"
                type="text"
                :placeholder="profileUser.username"
              >
              <span v-else class="profile-value">{{ profileUser.username || '-' }}</span>
            </div>

            <div class="profile-field">
              <label for="profile-first-name">First name</label>
              <input
                v-if="edit"
                id="profile-first-name"
                v-model="first_name"
                class="profile-input"
                type="text"
                :placeholder="profileUser.first_name"
              >
              <span v-else class="profile-value">{{ profileUser.first_name || '-' }}</span>
            </div>

            <div class="profile-field">
              <label for="profile-last-name">Last name</label>
              <input
                v-if="edit"
                id="profile-last-name"
                v-model="last_name"
                class="profile-input"
                type="text"
                :placeholder="profileUser.last_name"
              >
              <span v-else class="profile-value">{{ profileUser.last_name || '-' }}</span>
            </div>
          </div>

          <div v-if="saveError" class="profile-error" role="alert">
            {{ saveError }}
          </div>

          <div v-if="edit" class="profile-actions">
            <button
              type="button"
              class="profile-secondary-button"
              :disabled="saving"
              @click="cancelEdit"
            >
              Cancel
            </button>
            <button type="submit" class="profile-primary-button" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save changes' }}
            </button>
          </div>
        </form>
      </section>

      <aside class="profile-settings-side">
        <section class="profile-panel profile-security-panel">
          <div class="profile-panel-topline"></div>
          <div class="profile-section-heading profile-section-heading--compact">
            <div>
              <div class="profile-section-label">Security</div>
              <h2>Sign-in & session</h2>
            </div>
          </div>

          <div class="profile-security-list">
            <div class="profile-security-item">
              <span class="profile-security-icon icon icon-lock"></span>
              <div>
                <span>Sign-in method</span>
                <strong>{{ signInMethod }}</strong>
              </div>
            </div>
            <div class="profile-security-item">
              <span class="profile-security-icon profile-security-icon--active"></span>
              <div>
                <span>Current session</span>
                <strong>Active</strong>
              </div>
            </div>
          </div>
          <p class="profile-security-note">
            Password recovery and account verification are handled securely through your email.
          </p>

          <button
            type="button"
            class="profile-password-button"
            :aria-expanded="showPasswordReset"
            aria-controls="password-update-panel"
            @click="togglePasswordReset"
          >
            <span class="icon icon-key"></span>
            {{ showPasswordReset ? 'Cancel password update' : 'Update password' }}
          </button>

          <div v-if="showPasswordReset" id="password-update-panel" class="profile-password-reset">
            <p>
              We’ll send a secure password-update link to
              <strong>{{ profileUser.email }}</strong>.
            </p>
            <TurnstileWidget
              ref="passwordCaptcha"
              action="password-reset"
              size="flexible"
              @verified="passwordCaptchaToken = $event || ''"
              @expired="passwordCaptchaToken = ''"
              @error="passwordCaptchaToken = ''"
            />
            <div
              v-if="passwordResetNotice"
              class="profile-password-notice"
              :class="'is-' + passwordResetNotice.type"
              role="status"
            >
              {{ passwordResetNotice.text }}
            </div>
            <button
              type="button"
              class="profile-primary-button profile-send-reset"
              :disabled="passwordResetSending || !passwordCaptchaToken || !profileUser.email"
              @click="sendPasswordUpdate"
            >
              {{ passwordResetSending ? 'Sending...' : 'Send secure link' }}
            </button>
          </div>
        </section>

        <section class="profile-panel profile-session-panel">
          <div>
            <div class="profile-section-label">Session</div>
            <h2>Account session</h2>
            <p>Use this on shared or public devices.</p>
          </div>
          <button type="button" class="profile-logout-button" :disabled="loggingOut" @click="logoutUser">
            <span class="icon icon-log-out"></span>
            {{ loggingOut ? 'Logging out...' : 'Log out' }}
          </button>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import store from "../../store";
import TurnstileWidget from "./TurnstileWidget.vue";
import NotificationPreferencesPanel from "./shared/NotificationPreferencesPanel.vue";

const TABS = [
  { key: "notifications", label: "Notifications" },
  { key: "account", label: "Account" },
];

export default {
  name: "Profile",
  components: { TurnstileWidget, NotificationPreferencesPanel },
  data() {
    return {
      email: "",
      username: "",
      first_name: "",
      last_name: "",
      img_url: "",
      data: {},
      edit: false,
      saving: false,
      loggingOut: false,
      showPasswordReset: false,
      passwordResetSending: false,
      passwordCaptchaToken: "",
      passwordResetNotice: null,
      saveError: "",
      avatarError: false,
      tabs: TABS,
    };
  },
  computed: {
    ...mapGetters(["getUser"]),
    tab() {
      const wanted = this.$route && this.$route.query ? this.$route.query.tab : "";
      return TABS.some((item) => item.key === wanted) ? wanted : TABS[0].key;
    },
    profileUser() {
      if (this.getUser && typeof this.getUser === "object") {
        return this.getUser;
      }
      if (typeof this.getUser === "string") {
        try {
          return JSON.parse(this.getUser) || {};
        } catch (error) {
          return {};
        }
      }
      return {};
    },
    avatarUrl() {
      // Google sign-in stores the account photo on img_url. Fall back to initials
      // if it's missing or the image fails to load.
      if (this.avatarError) {
        return "";
      }
      return this.profileUser.img_url || "";
    },
    displayName() {
      const fullName = [this.profileUser.first_name, this.profileUser.last_name]
        .filter(Boolean)
        .join(" ");

      return fullName || this.profileUser.username || "GixCore User";
    },
    initials() {
      const source = this.displayName || this.profileUser.email || "GC";
      return source
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
    signInMethod() {
      const provider = String(this.profileUser.provider || this.profileUser.auth_provider || "").toLowerCase();
      if (provider === "google" || this.profileUser.google_id) {
        return "Google account";
      }
      return "Email and password";
    },
  },
  methods: {
    setTab(key) {
      if (key === this.tab) return;
      this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {});
    },
    startEdit() {
      this.email = this.profileUser.email || "";
      this.username = this.profileUser.username || "";
      this.first_name = this.profileUser.first_name || "";
      this.last_name = this.profileUser.last_name || "";
      this.img_url = this.profileUser.img_url || "";
      this.saveError = "";
      this.edit = true;
    },
    cancelEdit() {
      this.edit = false;
      this.data = {};
      this.saveError = "";
    },
    async handleSave() {
      if (this.saving) {
        return;
      }

      this.saveError = "";
      this.saving = true;

      try {
        await this.handleUpdateForm();
        await store.dispatch("validate");
        this.edit = false;
        this.data = {};
      } catch (error) {
        this.saveError = this.formatProfileError(error);
      } finally {
        this.saving = false;
      }
    },
    handleUpdateForm() {
      this.refreshFields();
      return store.dispatch("update", this.data);
    },
    refreshFields() {
      this.data = {
        email: this.email || this.profileUser.email,
        username: this.username || this.profileUser.username,
        first_name: this.first_name || this.profileUser.first_name,
        last_name: this.last_name || this.profileUser.last_name,
        img_url: this.img_url || this.profileUser.img_url,
      };
    },
    formatProfileError(error) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      if (error && error.message) {
        return error.message;
      }

      return "We could not save your profile changes.";
    },
    async logoutUser() {
      if (this.loggingOut) return;
      this.loggingOut = true;
      try {
        await store.dispatch("logout");
      } finally {
        this.$router.push("/").catch(() => {});
        this.loggingOut = false;
      }
    },
    togglePasswordReset() {
      this.showPasswordReset = !this.showPasswordReset;
      this.passwordCaptchaToken = "";
      this.passwordResetNotice = null;
    },
    resetPasswordCaptcha() {
      this.passwordCaptchaToken = "";
      if (this.$refs.passwordCaptcha) {
        this.$refs.passwordCaptcha.reset();
      }
    },
    async sendPasswordUpdate() {
      if (this.passwordResetSending || !this.passwordCaptchaToken || !this.profileUser.email) return;

      this.passwordResetSending = true;
      this.passwordResetNotice = null;
      try {
        await store.dispatch("forgotPassword", {
          email: this.profileUser.email,
          captchaToken: this.passwordCaptchaToken,
        });
        this.passwordResetNotice = {
          type: "success",
          text: "Check your inbox for the secure password-update link.",
        };
      } catch (error) {
        this.passwordResetNotice = {
          type: "error",
          text: this.formatProfileError(error),
        };
      } finally {
        this.passwordResetSending = false;
        this.resetPasswordCaptcha();
      }
    },
  },
};
</script>

<style scoped>
.profile-page {
	width: 100%;
	max-width: 1180px;
	padding: 0 0 3.5rem;
	color: #cfd2da;
}

.profile-tabs {
	display: flex;
	gap: 4px;
	margin: 0 0 1rem;
	border-bottom: 1px solid rgba(69, 82, 104, 0.6);
}

.profile-tab {
	margin-bottom: -1px;
	padding: 9px 14px;
	border: 0;
	border-bottom: 2px solid transparent;
	background: transparent;
	color: #8994a8;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
}

.profile-tab:hover { color: #dfe4ee; }
.profile-tab.active { border-bottom-color: #20b8eb; color: #eef2f7; }
.profile-tab:focus-visible { outline: 2px solid rgba(127, 208, 238, 0.6); outline-offset: -2px; }

.profile-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 2rem;
	margin-bottom: 1.25rem;
	padding: 0 0.15rem;
}

.profile-kicker,
.profile-section-label {
	color: #1997c6;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.8px;
	line-height: 1;
	text-transform: uppercase;
}

.profile-header h1 {
	margin: 0.5rem 0 0;
	color: #eef4fb;
	font-size: 32px;
	font-weight: 600;
	letter-spacing: 0;
	line-height: 1.1;
}

.profile-header p {
	margin: 0.55rem 0 0;
	max-width: 620px;
	color: #8f9aab;
	font-size: 14px;
	line-height: 1.45;
}

.profile-settings-grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(340px, 380px);
	gap: 1rem;
	align-items: start;
}

.profile-settings-side {
	display: grid;
	gap: 1rem;
}

.profile-status {
	display: inline-flex;
	align-items: center;
	gap: 0.7rem;
	min-width: 174px;
	min-height: 48px;
	padding: 0 0.9rem;
	border: 1px solid rgba(51, 91, 108, 0.68);
	border-radius: 9px;
	background: linear-gradient(135deg, rgba(17, 42, 51, 0.54), rgba(7, 13, 19, 0.76));
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.profile-status-dot {
	width: 8px;
	height: 8px;
	flex: 0 0 auto;
	border-radius: 50%;
	background: #00bc8c;
	box-shadow: 0 0 12px rgba(0, 188, 140, 0.8);
}

.profile-status-copy {
	display: grid;
	gap: 3px;
}

.profile-status-copy small {
	color: #748497;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.08em;
	line-height: 1;
	text-transform: uppercase;
}

.profile-status-copy strong {
	color: #ccefe4;
	font-size: 12px;
	font-weight: 700;
	line-height: 1;
}

.profile-panel {
	position: relative;
	overflow: hidden;
	border: 1px solid rgba(69, 82, 104, 0.78);
	border-radius: 11px;
	background:
		radial-gradient(circle at 8% 0%, rgba(25, 151, 198, 0.1), transparent 34%),
		linear-gradient(145deg, rgba(16, 22, 32, 0.9), rgba(7, 11, 17, 0.9));
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.04),
		0 18px 42px rgba(0, 0, 0, 0.2);
}

.profile-panel-topline {
	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	display: block;
	height: 2px;
	background: linear-gradient(90deg, #20b8eb, rgba(32, 184, 235, 0.18) 38%, transparent 78%);
	opacity: 0.8;
}

.profile-section-heading {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	padding: 1.4rem 1.5rem 1.15rem;
}

.profile-section-heading--compact {
	padding-bottom: 1rem;
}

.profile-section-heading h2,
.profile-session-panel h2 {
	margin: 0.4rem 0 0;
	color: #eef2f7;
	font-size: 20px;
	font-weight: 600;
	line-height: 1.2;
}

.profile-section-heading p,
.profile-session-panel p {
	margin: 0.4rem 0 0;
	color: #8994a8;
	font-size: 12px;
	line-height: 1.5;
}

.profile-summary {
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	align-items: center;
	gap: 1.1rem;
	margin: 0 1.5rem;
	padding: 1.1rem 1.15rem;
	border: 1px solid rgba(54, 81, 99, 0.64);
	border-radius: 9px;
	background:
		radial-gradient(circle at 0 0, rgba(25, 151, 198, 0.13), transparent 42%),
		rgba(6, 12, 19, 0.66);
}

.profile-avatar {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	width: 64px;
	height: 64px;
	border: 1px solid rgba(25, 151, 198, 0.84);
	border-radius: 10px;
	background:
		linear-gradient(180deg, rgba(25, 151, 198, 0.22), rgba(8, 10, 15, 0.7)),
		#10161d;
	box-shadow: 0 0 26px rgba(25, 151, 198, 0.14);
	color: #f4f8fb;
	font-size: 20px;
	font-weight: 800;
	letter-spacing: 0;
}

.profile-avatar-img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.profile-identity {
	min-width: 0;
}

.profile-identity h2 {
	margin: 0.3rem 0 0;
	color: #f4f8fb;
	font-size: 22px;
	font-weight: 600;
	letter-spacing: 0;
	line-height: 1.2;
}

.profile-identity > span {
	color: #5fbddc;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.09em;
	text-transform: uppercase;
}

.profile-identity p {
	margin: 0.35rem 0 0;
	overflow: hidden;
	color: #9198aa;
	font-size: 14px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.profile-form {
	padding: 1.2rem 1.5rem 1.5rem;
}

.profile-security-panel {
	padding-bottom: 1.15rem;
}

.profile-security-list {
	overflow: hidden;
	margin: 0 1.2rem;
	border: 1px solid rgba(63, 78, 98, 0.72);
	border-radius: 9px;
	background: rgba(6, 11, 17, 0.48);
}

.profile-security-item {
	display: grid;
	grid-template-columns: 34px minmax(0, 1fr);
	align-items: center;
	gap: 0.8rem;
	margin: 0;
	padding: 0.85rem;
	border: 0;
	border-bottom: 1px solid rgba(63, 78, 98, 0.56);
	background: transparent;
}

.profile-security-item:last-child {
	border-bottom: 0;
}

.profile-security-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	border: 1px solid rgba(25, 151, 198, 0.46);
	border-radius: 8px;
	background: rgba(25, 151, 198, 0.08);
	color: #39b6df;
}

.profile-security-icon--active::before {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #1bc98e;
	box-shadow: 0 0 12px rgba(27, 201, 142, 0.7);
	content: "";
}

.profile-security-item div > span {
	display: block;
	color: #778399;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
}

.profile-security-item strong {
	display: block;
	margin-top: 0.2rem;
	color: #dfe5ed;
	font-size: 13px;
	font-weight: 600;
}

.profile-security-note {
	margin: 0.9rem 1.25rem 0 !important;
	color: #7f8a9d;
	font-size: 11px;
	line-height: 1.5;
}

.profile-password-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.45rem;
	width: calc(100% - 2.3rem);
	min-height: 39px;
	margin: 1rem 1.15rem 0;
	border: 1px solid rgba(25, 151, 198, 0.58);
	border-radius: 8px;
	background: rgba(25, 151, 198, 0.06);
	color: #47bde5;
	cursor: pointer;
	font-size: 11px;
	font-weight: 800;
	text-transform: uppercase;
}

.profile-password-button:hover,
.profile-password-button:focus {
	border-color: #1997c6;
	background: rgba(25, 151, 198, 0.14);
	color: #f4f8fb;
}

.profile-password-reset {
	display: grid;
	gap: 0.8rem;
	margin: 0.8rem 1.15rem 0;
	padding: 0.9rem;
	border: 1px solid rgba(67, 72, 87, 0.72);
	border-radius: 7px;
	background:
		radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.055), transparent 42%),
		rgba(7, 10, 16, 0.54);
}

.profile-password-reset > p {
	margin: 0;
	color: #919bad;
	font-size: 12px;
	line-height: 1.5;
}

.profile-password-reset > p strong {
	color: #dfe5ed;
	font-weight: 600;
	overflow-wrap: anywhere;
}

.profile-password-notice {
	padding: 0.65rem 0.7rem;
	border: 1px solid rgba(230, 71, 89, 0.48);
	border-radius: 6px;
	background: rgba(230, 71, 89, 0.08);
	color: #ef9da8;
	font-size: 11px;
	font-weight: 650;
	line-height: 1.45;
}

.profile-password-notice.is-success {
	border-color: rgba(27, 201, 142, 0.42);
	background: rgba(27, 201, 142, 0.08);
	color: #74d8b4;
}

.profile-send-reset {
	width: 100%;
	margin: 0;
}

.profile-session-panel {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	min-height: 92px;
	padding: 1.1rem 1.2rem;
	border-color: rgba(95, 72, 83, 0.72);
	background:
		radial-gradient(circle at 100% 100%, rgba(230, 71, 89, 0.07), transparent 44%),
		linear-gradient(145deg, rgba(16, 20, 29, 0.9), rgba(8, 11, 17, 0.9));
}

.profile-session-panel h2 {
	margin-bottom: 0.28rem;
	font-size: 15px;
	line-height: 1.2;
}

.profile-session-panel p {
	margin: 0;
	font-size: 12px;
	line-height: 1.35;
}

.profile-logout-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	gap: 0.42rem;
	min-width: 104px;
	min-height: 32px;
	padding: 0 0.78rem;
	border: 1px solid rgba(230, 71, 89, 0.46);
	border-radius: 8px;
	background: rgba(230, 71, 89, 0.035);
	color: #d98b96;
	cursor: pointer;
	font-size: 10.5px;
	font-weight: 800;
	text-transform: uppercase;
	transition: 160ms ease;
}

.profile-logout-button:hover,
.profile-logout-button:focus {
	border-color: rgba(230, 71, 89, 0.86);
	background: rgba(230, 71, 89, 0.14);
	color: #ffb2bc;
}

.profile-logout-button:disabled {
	cursor: wait;
	opacity: 0.6;
}

.profile-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0;
	overflow: hidden;
	margin-top: 0;
	border: 1px solid rgba(67, 78, 98, 0.74);
	border-radius: 9px;
	background: rgba(5, 9, 15, 0.42);
}

.profile-field {
	display: grid;
	gap: 0.5rem;
	min-height: 82px;
	padding: 0.9rem 1rem;
	border: 0;
	border-right: 1px solid rgba(67, 78, 98, 0.58);
	border-bottom: 1px solid rgba(67, 78, 98, 0.58);
	background: rgba(8, 12, 19, 0.28);
}

.profile-field:nth-child(even) {
	border-right: 0;
}

.profile-field:nth-last-child(-n + 2) {
	border-bottom: 0;
}

.profile-field label {
	margin: 0;
	color: #7f8b9e;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 0.07em;
	line-height: 1;
	text-transform: uppercase;
}

.profile-value {
	align-self: center;
	overflow-wrap: anywhere;
	color: #e7ebf2;
	font-size: 16px;
	font-weight: 550;
	line-height: 1.25;
}

.profile-input {
	width: 100%;
	min-height: 40px;
	padding: 0.58rem 0.7rem;
	border: 1px solid rgba(67, 72, 87, 0.96);
	border-radius: 6px;
	outline: none;
	background: rgba(6, 8, 12, 0.76);
	color: #e5e8ef;
	font-size: 15px;
	letter-spacing: 0;
	transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.profile-input::placeholder {
	color: rgba(145, 152, 170, 0.6);
}

.profile-input:hover {
	border-color: rgba(25, 151, 198, 0.55);
}

.profile-input:focus {
	border-color: #1997c6;
	background: rgba(8, 10, 15, 0.98);
	box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
}

.profile-security-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	margin-top: 1rem;
	padding: 1rem 0.85rem;
	border-top: 1px solid rgba(67, 72, 87, 0.66);
	border-bottom: 1px solid rgba(67, 72, 87, 0.66);
}

.profile-password-line {
	display: inline-flex;
	align-items: center;
	gap: 0.55rem;
	margin-top: 0.55rem;
	color: #cfd2da;
	font-size: 15px;
}

.profile-password-line .icon {
	color: #1997c6;
}

.profile-link {
	color: #1997c6;
	font-size: 13px;
	font-weight: 700;
	text-decoration: none;
	text-transform: uppercase;
}

.profile-link:hover,
.profile-link:focus {
	color: #f4f8fb;
	text-decoration: none;
}

.profile-error {
	margin-top: 1rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(255, 91, 91, 0.62);
	border-radius: 6px;
	background: rgba(120, 20, 28, 0.22);
	color: #ffd6d6;
	font-size: 13px;
	font-weight: 700;
	line-height: 1.35;
}

.profile-actions {
	display: flex;
	justify-content: flex-end;
	gap: 0.75rem;
	margin-top: 1.15rem;
}

.profile-ghost-button,
.profile-primary-button,
.profile-secondary-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 38px;
	padding: 0 1rem;
	border-radius: 8px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.3px;
	line-height: 1;
	text-transform: uppercase;
	transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease;
}

.profile-ghost-button,
.profile-secondary-button {
	border: 1px solid rgba(25, 151, 198, 0.64);
	background: rgba(25, 151, 198, 0.06);
	color: #cfd2da;
}

.profile-primary-button {
	border: 1px solid #16b4ed;
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.34), rgba(12, 66, 88, 0.64));
	box-shadow:
		0 0 24px rgba(25, 151, 198, 0.13),
		inset 0 0 18px rgba(25, 151, 198, 0.12);
	color: #f4f8fb;
}

.profile-ghost-button:hover,
.profile-ghost-button:focus,
.profile-secondary-button:hover,
.profile-secondary-button:focus {
	border-color: #1997c6;
	background: rgba(25, 151, 198, 0.16);
	color: #ffffff;
}

.profile-primary-button:hover,
.profile-primary-button:focus {
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.48), rgba(13, 88, 117, 0.78));
	box-shadow:
		0 0 30px rgba(25, 151, 198, 0.22),
		inset 0 0 18px rgba(25, 151, 198, 0.18);
	color: #ffffff;
}

.profile-primary-button:disabled,
.profile-secondary-button:disabled {
	cursor: not-allowed;
	opacity: 0.58;
}

@media screen and (max-width: 860px) {
	.profile-settings-grid {
		grid-template-columns: 1fr;
	}

	.profile-header,
	.profile-summary,
	.profile-security-row {
		align-items: flex-start;
		grid-template-columns: 1fr;
	}

	.profile-header,
	.profile-security-row {
		flex-direction: column;
	}

	.profile-session-panel {
		align-items: stretch;
		flex-direction: column;
	}

	.profile-logout-button {
		align-self: flex-start;
	}

	.profile-grid {
		grid-template-columns: 1fr;
	}

	.profile-field,
	.profile-field:nth-child(even),
	.profile-field:nth-last-child(-n + 2) {
		border-right: 0;
		border-bottom: 1px solid rgba(67, 78, 98, 0.58);
	}

	.profile-field:last-child {
		border-bottom: 0;
	}

	.profile-actions {
		flex-direction: column-reverse;
	}

	.profile-primary-button,
	.profile-secondary-button,
	.profile-ghost-button {
		width: 100%;
	}

	.profile-section-heading {
		align-items: stretch;
		flex-direction: column;
	}
}

@media screen and (max-width: 520px) {
	.profile-header h1 {
		font-size: 28px;
	}

	.profile-status {
		width: 100%;
	}

	.profile-section-heading,
	.profile-form {
		padding-right: 1rem;
		padding-left: 1rem;
	}

	.profile-summary {
		margin-right: 1rem;
		margin-left: 1rem;
		padding: 1rem;
	}

	.profile-security-list {
		margin-right: 1rem;
		margin-left: 1rem;
	}

	.profile-password-button {
		width: calc(100% - 2rem);
		margin-right: 1rem;
		margin-left: 1rem;
	}
}
</style>
